// Right-hand artifact drawer (design spec, turn 2 "desktop workspace").
//
// Artifacts are produced per exchange inside the chat column, but the spec puts
// them in a drawer that slides over the whole workspace and is toggled from the
// masthead. Rather than thread props from Chat up through AppShell into
// Masthead, pages register their artifacts into this context and the shell owns
// the presentation.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { useResizablePanel } from "@/hooks/useResizablePanel";
import type { Artifact } from "@/types/eryndal";
import { cn } from "@/utils/cn";

import { ArtifactDrawerContext, useArtifactDrawer } from "./artifactDrawerContext";

import { ArtifactBlock } from "../chat/ArtifactBlock";

export function ArtifactDrawerProvider({ children }: { children: ReactNode }) {
  const [artifacts, setArtifactsState] = useState<Artifact[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Replacing the list with an identical one would otherwise re-render every
  // consumer on each poll of the chat query.
  const setArtifacts = useCallback((next: Artifact[]) => {
    setArtifactsState((prev) => {
      if (prev.length === next.length && prev.every((a, i) => a === next[i])) return prev;
      return next;
    });
  }, []);

  const close = useCallback(() => setIsOpen(false), []);
  const open = useCallback(() => setIsOpen(true), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // An empty drawer should not be able to stay open — e.g. after switching to a
  // session that produced nothing.
  useEffect(() => {
    if (artifacts.length === 0) setIsOpen(false);
  }, [artifacts.length]);

  const value = useMemo(
    () => ({ artifacts, setArtifacts, isOpen, open, close, toggle }),
    [artifacts, setArtifacts, isOpen, open, close, toggle],
  );

  return (
    <ArtifactDrawerContext.Provider value={value}>{children}</ArtifactDrawerContext.Provider>
  );
}

const KIND_LABELS: Record<string, string> = {
  mermaid: "diagram",
  cytoscape: "graph",
  table: "table",
  markdown: "draft",
  xlsx: "workbook",
  docx: "document",
  pptx: "deck",
  pdf: "pdf",
};

/**
 * The sliding panel itself. Rendered once by AppShell; stays mounted so the
 * transform transition can run in both directions.
 */
export function ArtifactDrawer() {
  const { artifacts, isOpen, close } = useArtifactDrawer();
  const [selected, setSelected] = useState(0);

  // 440px is the spec width. Wide tables and diagrams land here, so the upper
  // bound is generous — the hook still refuses to pass 60% of the viewport.
  const panel = useResizablePanel({
    storageKey: "eryndal.panel.drawer",
    defaultWidth: 440,
    min: 320,
    max: 840,
    side: "right",
  });

  // Keep the selection in range when the artifact list changes underneath us.
  useEffect(() => {
    setSelected((i) => (i < artifacts.length ? i : 0));
  }, [artifacts.length]);

  // Escape closes, matching every other dismissible surface in the app.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  const active = artifacts[selected];

  return (
    <>
      {/* Scrim. Starts below the masthead so the Artifacts toggle stays live. */}
      <div
        aria-hidden={!isOpen}
        onClick={close}
        className={cn(
          "absolute inset-x-0 bottom-0 top-masthead z-40 bg-espresso-700/[0.18]",
          "transition-opacity duration-[420ms] ease-drawer",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      <aside
        aria-hidden={!isOpen}
        aria-label="Artifacts built in this run"
        style={{ width: panel.width }}
        className={cn(
          "absolute bottom-0 right-0 top-masthead z-50 flex flex-col",
          "border-l border-ruleline bg-parchment-50 shadow-drawer",
          // Width changes must not animate while dragging, or the panel lags
          // the pointer.
          panel.isDragging
            ? "transition-none"
            : "transition-transform duration-[420ms] ease-drawer",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Grab handle on the leading edge, mirroring the sidebar's. */}
        <div
          {...panel.separatorProps}
          aria-label="Resize artifacts drawer"
          title="Drag to resize · double-click to reset"
          className={cn(
            "absolute left-0 top-0 z-20 h-full w-1.5 cursor-col-resize",
            "transition-colors duration-200 ease-atelier hover:bg-oxblood-200",
            "focus-visible:bg-oxblood-300 focus-visible:outline-none",
            panel.isDragging && "bg-oxblood-300",
          )}
        />

        <header className="flex flex-none items-start justify-between gap-3 border-b border-ruleline px-5 py-4">
          <div>
            <span className="label-mono-muted">Built in this run</span>
            <h2 className="mt-0.5 font-display text-[1.2rem] leading-tight text-espresso-700">
              Documents &amp; diagrams
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close artifacts"
            className={cn(
              "flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full",
              "text-warmgray-400 transition duration-200 ease-atelier",
              "hover:rotate-90 hover:bg-parchment-100 hover:text-oxblood-500",
            )}
          >
            ✕
          </button>
        </header>

        {artifacts.length > 1 ? (
          <nav className="flex flex-none flex-col gap-0.5 border-b border-ruleline p-3">
            {artifacts.map((a, i) => (
              <button
                key={`${a.kind}-${i}`}
                type="button"
                onClick={() => setSelected(i)}
                className={cn(
                  "flex items-baseline gap-3 rounded-chip border-l-2 px-3 py-2 text-left",
                  "transition-colors duration-200 ease-atelier",
                  i === selected
                    ? "border-oxblood-500 bg-oxblood-50"
                    : "border-transparent hover:bg-parchment-100",
                )}
              >
                <span className="w-[62px] shrink-0 font-mono text-[0.62rem] uppercase tracking-[0.12em] text-oxblood-500">
                  {KIND_LABELS[a.kind] ?? a.kind}
                </span>
                <span className="min-w-0 flex-1 truncate font-serif text-sm text-espresso-700">
                  {a.title?.trim() || "Untitled"}
                </span>
              </button>
            ))}
          </nav>
        ) : null}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {active ? (
            <div key={selected} className="animate-ery-rise">
              <ArtifactBlock artifact={active} />
            </div>
          ) : (
            <p className="font-serif text-sm italic text-warmgray-400">
              Nothing built in this run yet.
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
